// De dónde vino un negocio que se registra en Redy. Puro: sin I/O, sin fecha
// del sistema (la recibe), testeable en Node.
//
// El visitante llega a useredy.com con `utm_*` (el «Powered by Redy» de una
// tienda, un anuncio, un correo) y casi nunca se registra en esa misma página:
// pasa por la home, precios, y recién después abre /signup, donde la URL ya no
// trae nada. Por eso la visita se guarda en una cookie propia al llegar
// (components/marketing/SignupAttributionCapture.tsx) y el alta la lee
// (app/api/auth/signup/route.ts → signup_attributions).
//
// Regla: último toque ETIQUETADO (utm_* o click-id de un anuncio). Una visita
// etiquetada reemplaza lo guardado; una que llega desde otro sitio sin
// etiquetas (Google orgánico, un blog) solo se guarda si no había nada; una
// visita directa no toca nada. Vence a los 30 días.
//
// Click-ids: `gclid` lo pone Google Ads en cada clic de un anuncio; `fbclid` lo
// pone Facebook/Instagram en los links que salen de la app — de anuncios Y de
// publicaciones orgánicas, así que fbclid solo dice «vino de Meta», no «vino
// de un anuncio». `gbraid` / `wbraid` son los que pone Google EN LUGAR de gclid
// cuando el clic viene de iOS (gbraid: anuncio abierto en una app; wbraid: en
// la web de Safari), por las reglas de privacidad de Apple. Se guardan crudos para poder subirle la conversión a cada
// plataforma después (Google: offline conversions; Meta: `fbc` =
// fb.1.<ms de touched_at>.<fbclid>).

import { REFERRAL_CODE_RE } from "@/lib/referrals/codes"

export const SIGNUP_ATTR_COOKIE = "redy_signup_attr"
export const SIGNUP_ATTR_MAX_AGE_S = 30 * 24 * 60 * 60

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const
export type UtmKey = (typeof UTM_KEYS)[number]

export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"] as const
/** Los que identifican un clic de Google Ads (gclid, o su reemplazo en iOS). */
export const GOOGLE_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number]

export type SignupAttribution = Partial<Record<UtmKey, string>> & Partial<Record<ClickIdKey, string>> & {
  /** Host de la página anterior si era OTRO sitio (nunca la URL completa). */
  referrer_host?: string
  /** Ruta de useredy.com donde aterrizó, sin query. */
  landing_path?: string
  /** Cuándo fue la visita (ISO). */
  touched_at: string
  /** Código de referido (programa de referidos): 6 de BCDFGHJKMNPQRSTVWXZ23456789. */
  ref?: string
  /** Primer toque con ESE ref (ISO). Un ref distinto lo reinicia. */
  ref_first_seen_at?: string
}

const MAX_VALUE = 200
const MAX_PATH = 300
/** Un gclid/gbraid/wbraid real ronda 100 caracteres y un fbclid 60–200; 500 deja margen. */
const MAX_CLICK_ID = 500
/** Base64url y punto: lo que usan las dos plataformas. Lo demás es basura o un intento de inyectar algo. */
const CLICK_ID_RE = /^[A-Za-z0-9_.-]+$/

function cleanClickId(value: unknown): string | undefined {
  const v = clean(value, MAX_CLICK_ID + 1)
  return v && v.length <= MAX_CLICK_ID && CLICK_ID_RE.test(v) ? v : undefined
}

/** Hosts propios: un referrer desde aquí es navegación interna, no un origen. */
function isOwnHost(host: string, ownHost: string): boolean {
  const strip = (h: string) => h.toLowerCase().replace(/^www\./, "")
  const h = strip(host)
  const own = strip(ownHost)
  return h === own || h.endsWith(`.${own}`)
}

function cleanRef(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const v = value.trim().toUpperCase()
  return REFERRAL_CODE_RE.test(v) ? v : undefined
}

function clean(value: unknown, max = MAX_VALUE): string | undefined {
  if (typeof value !== "string") return undefined
  // Sin caracteres de control: el valor termina en un panel y en un correo.
  const v = value.replace(/[\u0000-\u001f\u007f]/g, "").trim()
  return v ? v.slice(0, max) : undefined
}

function hostOf(referrer: string | null | undefined): string | undefined {
  if (!referrer) return undefined
  try {
    const u = new URL(referrer)
    return u.protocol === "http:" || u.protocol === "https:" ? clean(u.hostname.toLowerCase()) : undefined
  } catch {
    return undefined
  }
}

/** Lo que dice ESTA visita, o null si no aporta nada (visita directa o interna). */
export function attributionFromVisit(visit: {
  search: string
  pathname: string
  referrer: string | null | undefined
  ownHost: string
  now: Date
}): SignupAttribution | null {
  const params = new URLSearchParams(visit.search)
  const utm: Partial<Record<UtmKey, string>> = {}
  for (const k of UTM_KEYS) {
    const v = clean(params.get(k))
    if (v) utm[k] = v
  }
  const clicks: Partial<Record<ClickIdKey, string>> = {}
  for (const k of CLICK_ID_KEYS) {
    const v = cleanClickId(params.get(k))
    if (v) clicks[k] = v
  }
  const ref = cleanRef(params.get("ref"))
  const refHost = hostOf(visit.referrer)
  const referrer_host = refHost && !isOwnHost(refHost, visit.ownHost) ? refHost : undefined
  if (Object.keys(utm).length === 0 && Object.keys(clicks).length === 0 && !referrer_host && !ref) return null
  return {
    ...utm,
    ...clicks,
    ...(referrer_host ? { referrer_host } : {}),
    landing_path: clean(visit.pathname, MAX_PATH) ?? "/",
    touched_at: visit.now.toISOString(),
    ...(ref ? { ref, ref_first_seen_at: visit.now.toISOString() } : {}),
  }
}

export function hasUtm(a: SignupAttribution | null | undefined): boolean {
  return !!a && UTM_KEYS.some((k) => !!a[k])
}

export function hasClickId(a: SignupAttribution | null | undefined): boolean {
  return !!a && CLICK_ID_KEYS.some((k) => !!a[k])
}

/** Etiquetada = trae utm_* , un click-id o un ref de referido: es un toque que se puede atribuir. */
/**
 * La misma atribución sin identificadores de clic (gclid/gbraid/wbraid/fbclid).
 * Con exclusión («Your Privacy Choices» o Global Privacy Control) no se guardan
 * ni se suben a Meta/Google: subirlos es «compartir» bajo la ley de California.
 * Lo first-party (utm_*, referrer, landing, ref) se queda. Auditoría 2026-10-14.
 */
export function withoutClickIds(a: SignupAttribution): SignupAttribution {
  const out: SignupAttribution = { ...a }
  for (const k of CLICK_ID_KEYS) delete out[k]
  return out
}

export function isTagged(a: SignupAttribution | null | undefined): boolean {
  return hasUtm(a) || hasClickId(a) || !!a?.ref
}

/** Qué queda guardado después de esta visita (null = no escribir nada). */
export function nextAttribution(
  stored: SignupAttribution | null,
  visit: SignupAttribution | null,
): SignupAttribution | null {
  if (!visit) return null
  // Una visita solo con ref (sin UTM ni click-id) no es un origen de marketing:
  // se FUSIONA con lo guardado para no perder los click-ids del anuncio.
  if (stored && !hasUtm(visit) && !hasClickId(visit) && visit.ref) {
    return {
      ...stored,
      ref: visit.ref,
      ref_first_seen_at: stored.ref === visit.ref && stored.ref_first_seen_at ? stored.ref_first_seen_at : visit.ref_first_seen_at,
    }
  }
  const base = isTagged(visit) ? visit : stored ? null : visit
  if (!base) return null
  // El ref guardado sobrevive a una visita etiquetada sin ref (retargeting);
  // un ref nuevo reemplaza; el mismo ref conserva su primer toque.
  if (!base.ref && stored?.ref) return { ...base, ref: stored.ref, ref_first_seen_at: stored.ref_first_seen_at }
  if (base.ref && stored?.ref === base.ref && stored.ref_first_seen_at) return { ...base, ref_first_seen_at: stored.ref_first_seen_at }
  return base
}

/**
 * Lee la cookie (o cualquier valor que diga serlo) y devuelve solo lo
 * permitido, saneado. Viene del navegador: cualquiera puede escribirla a mano,
 * así que nunca se confía en su forma.
 */
export function decodeAttribution(raw: string | null | undefined): SignupAttribution | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(decodeURIComponent(raw))
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null
  const o = parsed as Record<string, unknown>
  const touched = typeof o.touched_at === "string" ? new Date(o.touched_at) : null
  if (!touched || Number.isNaN(touched.getTime())) return null
  const out: SignupAttribution = { touched_at: touched.toISOString() }
  for (const k of UTM_KEYS) {
    const v = clean(o[k])
    if (v) out[k] = v
  }
  for (const k of CLICK_ID_KEYS) {
    const v = cleanClickId(o[k])
    if (v) out[k] = v
  }
  const refHost = clean(o.referrer_host)
  if (refHost && /^[a-z0-9.-]+$/i.test(refHost)) out.referrer_host = refHost.toLowerCase()
  const path = clean(o.landing_path, MAX_PATH)
  if (path && path.startsWith("/")) out.landing_path = path
  const ref = cleanRef(o.ref)
  if (ref) {
    const seen = typeof o.ref_first_seen_at === "string" ? new Date(o.ref_first_seen_at) : null
    out.ref = ref
    out.ref_first_seen_at = seen && !Number.isNaN(seen.getTime()) ? seen.toISOString() : out.touched_at
  }
  if (!isTagged(out) && !out.referrer_host) return null
  return out
}

export function encodeAttribution(a: SignupAttribution): string {
  return encodeURIComponent(JSON.stringify(a))
}

/** Valor de una cookie a partir del header `Cookie` (sin depender de NextRequest). */
export function readCookie(header: string | null | undefined, name: string): string | null {
  if (!header) return null
  for (const part of header.split(";")) {
    const i = part.indexOf("=")
    if (i === -1) continue
    if (part.slice(0, i).trim() === name) return part.slice(i + 1).trim()
  }
  return null
}

/** Fila para `signup_attributions`. */
export function attributionRow(merchantId: string, a: SignupAttribution) {
  return {
    merchant_id: merchantId,
    utm_source: a.utm_source ?? null,
    utm_medium: a.utm_medium ?? null,
    utm_campaign: a.utm_campaign ?? null,
    utm_content: a.utm_content ?? null,
    utm_term: a.utm_term ?? null,
    gclid: a.gclid ?? null,
    gbraid: a.gbraid ?? null,
    wbraid: a.wbraid ?? null,
    fbclid: a.fbclid ?? null,
    referrer_host: a.referrer_host ?? null,
    landing_path: a.landing_path ?? null,
    touched_at: a.touched_at,
  }
}
